import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import Editor from '@monaco-editor/react';
import {
  Eye, Code2, Palette, Download, FileCode, FileText, Upload, Image as ImageIcon,
  CheckCircle2, MousePointer2, Check, X, Loader2, Send, Globe, ChevronRight, ChevronDown, Type,
  Folder, FolderOpen, File, Save, Undo2, Redo2, History, Search, Navigation, Layers, LayoutTemplate, Mail
} from 'lucide-react';
import { toast } from 'sonner';
import { buildSrcDoc } from '@/lib/buildSrcDoc';
import { FontSearchPicker } from '@/components/ui/font-search-picker';
import ViewportToggle from './ViewportToggle';
import VisualEditPanel from './VisualEditPanel';
import SeoPanel from './SeoPanel';
import NavEditorPanel from './NavEditorPanel';
import LayersPanel from './LayersPanel';
import SectionPicker from './SectionPicker';
import { GOOGLE_FONTS } from '@/lib/fonts';
import VariableFontAxesControl from './VariableFontAxesControl';
import MeshGradientEditor from './MeshGradientEditor';
import { ColorPicker as ColorPickerImport } from '@/components/ui/color-picker';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;


// ─── File Tree Component ───
function FileTreeNode({ node, activeFile, onSelect, depth = 0 }) {
  const [open, setOpen] = useState(true);
  const isFolder = node.type === 'folder';
  const isActive = !isFolder && node.path === activeFile;
  const paddingLeft = 8 + depth * 14;

  if (isFolder) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className="w-full flex items-center gap-1.5 py-1 text-xs text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] hover:bg-[var(--orbis-s2)] transition-colors"
          style={{ paddingLeft }}
        >
          {open ? <FolderOpen className="w-3.5 h-3.5 text-yellow-500/70 shrink-0" /> : <Folder className="w-3.5 h-3.5 text-yellow-500/70 shrink-0" />}
          <span className="truncate">{node.name}</span>
        </button>
        {open && node.children?.map((child, i) => (
          <FileTreeNode key={i} node={child} activeFile={activeFile} onSelect={onSelect} depth={depth + 1} />
        ))}
      </div>
    );
  }

  const ext = node.name.split('.').pop();
  const iconColor = ext === 'html' ? 'text-orange-400' : ext === 'css' ? 'text-blue-400' : ext === 'js' ? 'text-yellow-400' : 'text-[var(--orbis-t4)]';

  return (
    <button
      onClick={() => onSelect(node.path)}
      className={`w-full flex items-center gap-1.5 py-1 text-xs transition-colors ${
        isActive ? 'bg-[var(--orbis-s2)] text-[var(--orbis-t1)]' : 'text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] hover:bg-[var(--orbis-s2)]'
      }`}
      style={{ paddingLeft }}
    >
      <FileCode className={`w-3.5 h-3.5 shrink-0 ${iconColor}`} />
      <span className="truncate">{node.name}</span>
    </button>
  );
}

// ─── Editable Color Swatch ───
function EditableColorSwatch({ color, label, onChange, disabled, projectId }) {
  return (
    <div className="flex items-center gap-2">
      <ColorPickerImport
        value={color || '#000000'}
        onValueChange={(newColor) => !disabled && onChange(newColor)}
        mode="solid"
        projectId={projectId}
      >
        <button
          type="button"
          disabled={disabled}
          className="w-6 h-6 rounded shrink-0 border border-[rgba(255,255,255,0.12)] transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ background: color || 'transparent' }}
        />
      </ColorPickerImport>
      <span className="text-[11px] text-[var(--orbis-t3)]">{label}</span>
      <span className="text-[10px] text-[var(--orbis-t4)] font-mono ml-auto">{color}</span>
    </div>
  );
}

// ─── Collapsible sidebar section ───
function SidebarSection({ title, icon: Icon, iconColor, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[rgba(255,255,255,0.07)]">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-[var(--orbis-s1)] transition-colors"
      >
        <span className="flex items-center gap-2 text-sm font-medium text-[var(--orbis-t1)]">
          {Icon && <Icon className={`w-4 h-4 ${iconColor || 'text-[var(--orbis-t3)]'}`} />}
          {title}
        </span>
        <ChevronDown className={`w-3.5 h-3.5 text-[var(--orbis-t4)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-4 pb-3">{children}</div>}
    </div>
  );
}

export default function ReviewStage({
  code,
  designSystem,
  generationMode,
  generatedPages,
  projectId,
  onApprove,
  onDownload,
  onEdit,
  isProcessing,
  referenceReport,
  mode = 'magic',
  modeContext = {},
  reviewCapabilities = {},
}) {
  const [activeTab, setActiveTab] = useState('preview');
  const [activeCodeTab, setActiveCodeTab] = useState('html');
  const [currentPageSlug, setCurrentPageSlug] = useState('home');
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedElement, setSelectedElement] = useState(null);
  const [editRequest, setEditRequest] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [viewport, setViewport] = useState('desktop');
  const [isGlobalEditing, setIsGlobalEditing] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  // Monaco editor state
  const [virtualFiles, setVirtualFiles] = useState(null);
  const [activeFilePath, setActiveFilePath] = useState('index.html');
  const [editorDirty, setEditorDirty] = useState(false);
  const [isSavingCode, setIsSavingCode] = useState(false);
  const [isUndoing, setIsUndoing] = useState(false);
  const [editHistoryOpen, setEditHistoryOpen] = useState(false);
  const [showLayers, setShowLayers] = useState(false);
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [isSavingVersion, setIsSavingVersion] = useState(false);
  const [isRestoringVersion, setIsRestoringVersion] = useState(false);
  const [isPreviewOpening, setIsPreviewOpening] = useState(false);
  const [aiPreview, setAiPreview] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [copiedStylePayload, setCopiedStylePayload] = useState(null);
  const [showMeshEditor, setShowMeshEditor] = useState(false);
  // Form settings state
  const [formSettings, setFormSettings] = useState({ recipient_email: '', mode: 'orbis', embed_code: '' });
  const [isSavingFormSettings, setIsSavingFormSettings] = useState(false);
  const [isSendingTestForm, setIsSendingTestForm] = useState(false);
  const saveTimeoutRef = useRef(null);
  const iframeRef = useRef(null);
  const logoInputRef = useRef(null);
  const currentPageSlugRef = useRef('home');
  const colors = designSystem?.colors || {};
  const typography = designSystem?.typography || {};
  const isMultiPage = generationMode === 'multi_page' && generatedPages?.length > 0;
  const activeReferenceReport = mode === 'clone' ? (modeContext?.referenceReport || referenceReport) : null;
  const templateInfo = mode === 'template' ? (modeContext?.template || null) : null;
  const magicContext = mode === 'magic' ? modeContext : null;

  // Keep slug ref in sync for use inside message handlers (avoids stale closure)
  useEffect(() => { currentPageSlugRef.current = currentPageSlug; }, [currentPageSlug]);

  const fetchVersions = useCallback(async () => {
    setVersionsLoading(true);
    try {
      const resp = await axios.get(`${API_URL}/magic/projects/${projectId}/review/versions`);
      setVersions(resp.data?.versions || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load versions');
    } finally {
      setVersionsLoading(false);
    }
  }, [projectId]);

  // Save code to backend
  const saveCode = useCallback(async (filePath, content) => {
    setIsSavingCode(true);
    try {
      const resp = await axios.post(`${API_URL}/magic/projects/${projectId}/save-code`, {
        file_path: filePath,
        content
      });
      setEditorDirty(false);
      // Pass full magic_state back up so page container stays in sync
      if (onEdit && resp.data?.magic_state) {
        onEdit(resp.data.magic_state);
      }
      toast.success('Code saved');
    } catch (e) {
      toast.error('Failed to save: ' + (e.response?.data?.detail || e.message));
    } finally {
      setIsSavingCode(false);
    }
  }, [projectId, onEdit]);

  useEffect(() => {
    if (versionsOpen) fetchVersions();
  }, [versionsOpen, fetchVersions]);

  useEffect(() => {
    setAiPreview(null);
  }, [currentPageSlug]);

  useEffect(() => {
    const closeMenus = () => setContextMenu(null);
    window.addEventListener('click', closeMenus);
    return () => window.removeEventListener('click', closeMenus);
  }, []);

  // Build virtual files from generated pages (client-side fallback if backend hasn't provided them)
  useEffect(() => {
    async function fetchVirtualFiles() {
      try {
        const resp = await axios.get(`${API_URL}/projects/${projectId}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` }
        });
        const ms = resp.data?.magic_state || {};
        const vf = ms.virtual_files;
        // Load form settings from magic_state
        if (ms.form_settings) {
          setFormSettings(prev => ({ ...prev, ...ms.form_settings }));
        }
        if (vf && Object.keys(vf.files || {}).length > 0) {
          setVirtualFiles(vf);
          return;
        }
      } catch (e) { /* fall through to client-side build */ }

      // Client-side fallback: build virtual files from props
      if (!generatedPages?.length && !code?.html) return;
      const files = {};
      const tree = [];
      if (isMultiPage && generatedPages?.length) {
        generatedPages.forEach(p => {
          const fn = p.slug === 'home' ? 'index.html' : `${p.slug}.html`;
          files[fn] = { content: p.html || '', language: 'html', editable: true, slug: p.slug, name: p.name };
          tree.push({ name: fn, type: 'file', path: fn });
        });
      } else if (code?.html) {
        files['index.html'] = { content: code.html, language: 'html', editable: true, slug: 'home', name: 'Home' };
        tree.push({ name: 'index.html', type: 'file', path: 'index.html' });
        if (code.css) {
          files['style.css'] = { content: code.css, language: 'css', editable: true };
          tree.push({ name: 'style.css', type: 'file', path: 'style.css' });
        }
        if (code.js) {
          files['script.js'] = { content: code.js, language: 'javascript', editable: true };
          tree.push({ name: 'script.js', type: 'file', path: 'script.js' });
        }
      }
      setVirtualFiles({ files, tree });
    }
    fetchVirtualFiles();
  }, [projectId, generatedPages, code, isMultiPage]);

  const handleCreateVersion = useCallback(async () => {
    const label = window.prompt('Version name', `Checkpoint ${new Date().toLocaleString()}`);
    if (!label) return;
    setIsSavingVersion(true);
    try {
      const resp = await axios.post(`${API_URL}/magic/projects/${projectId}/review/versions`, { label });
      setVersions(resp.data?.versions || []);
      if (resp.data?.magic_state && onEdit) onEdit(resp.data.magic_state);
      toast.success('Version saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save version');
    } finally {
      setIsSavingVersion(false);
    }
  }, [projectId, onEdit]);

  const handleRestoreVersion = useCallback(async (versionId) => {
    if (!versionId) return;
    const confirmed = window.confirm('Restore this version? Current draft edits will be replaced.');
    if (!confirmed) return;
    setIsRestoringVersion(true);
    try {
      const resp = await axios.post(`${API_URL}/magic/projects/${projectId}/review/versions/${encodeURIComponent(versionId)}/restore`);
      if (resp.data?.magic_state && onEdit) onEdit(resp.data.magic_state);
      setAiPreview(null);
      setSelectedElement(null);
      setVersions(resp.data?.versions || []);
      toast.success('Version restored');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to restore version');
    } finally {
      setIsRestoringVersion(false);
    }
  }, [projectId, onEdit]);

  const handleOpenBrowserPreview = useCallback(() => {
    setIsPreviewOpening(true);
    axios.post(`${API_URL}/magic/projects/${projectId}/review/preview-session`, {
      page_slug: currentPageSlugRef.current || 'home',
    })
      .then((resp) => {
        const previewUrl = resp.data?.preview_url;
        if (!previewUrl) {
          throw new Error('Preview unavailable');
        }
        const absoluteUrl = previewUrl.startsWith('http')
          ? previewUrl
          : `${process.env.REACT_APP_BACKEND_URL}${previewUrl}`;
        window.open(absoluteUrl, '_blank', 'noopener,noreferrer');
      })
      .catch((error) => {
        toast.error(error.response?.data?.detail || error.message || 'Failed to open preview');
      })
      .finally(() => {
        setIsPreviewOpening(false);
      });
  }, [projectId]);

  // Monaco editor change handler with debounced auto-save
  const handleEditorChange = useCallback((value) => {
    if (!virtualFiles || !activeFilePath) return;
    // Update local virtual file content
    setVirtualFiles(prev => ({
      ...prev,
      files: {
        ...prev.files,
        [activeFilePath]: { ...prev.files[activeFilePath], content: value }
      }
    }));
    setEditorDirty(true);
    // Debounce auto-save (2 seconds)
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveCode(activeFilePath, value);
    }, 2000);
  }, [activeFilePath, virtualFiles, saveCode]);

  // Cmd+S / Ctrl+S immediate save
  const handleEditorMount = useCallback((editor, monaco) => {
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      const value = editor.getValue();
      saveCode(activeFilePath, value);
    });
  }, [activeFilePath, saveCode]);

  const currentPageData = isMultiPage
    ? (generatedPages.find(p => p.slug === currentPageSlug) || generatedPages[0])
    : null;

  const displayHtml = aiPreview?.updatedPage?.html || (isMultiPage ? currentPageData?.html : code?.html);
  const displayCss = aiPreview?.updatedPage?.css || (isMultiPage ? currentPageData?.css : code?.css);
  const displayJs = aiPreview?.updatedPage?.js || (isMultiPage ? currentPageData?.js : code?.js);

  const srcDoc = buildSrcDoc(displayHtml, displayCss, displayJs);

  // Track if iframe script is ready
  const iframeReadyRef = useRef(false);

  // Listen for iframe messages
  useEffect(() => {
    const handleMessage = (e) => {
      if (!e.data?.type) return;

      if (e.data.type === 'wf_iframe_ready') {
        iframeReadyRef.current = true;
        if (isSelectMode) {
          iframeRef.current?.contentWindow?.postMessage({ type: 'wf_set_select_mode', value: true }, '*');
        }
        return;
      }

      if (e.data.type === 'wf_navigate' && isMultiPage && !isSelectMode) {
        const slug = (e.data.href || 'home').toLowerCase();
        const matched = generatedPages?.find(p => p.slug === slug)
          || generatedPages?.find(p => p.slug.toLowerCase() === slug)
          || generatedPages?.find(p => slug.includes(p.slug.toLowerCase()))
          || generatedPages?.find(p => p.slug.toLowerCase().includes(slug))
          || generatedPages?.find(p => p.name.toLowerCase().replace(/\s+/g, '-') === slug);
        if (matched) {
          setCurrentPageSlug(matched.slug);
          setSelectedElement(null);
        }
      } else if (e.data.type === 'wf_select') {
        setSelectedElement({
          outerHTML: e.data.outerHTML,
          tagName: e.data.tagName,
          classList: e.data.classList,
          textContent: e.data.textContent,
          computedStyles: e.data.computedStyles || {},
          cssPath: e.data.cssPath || null,
          href: e.data.href || null
        });
        setContextMenu(null);
      } else if (e.data.type === 'wf_context_menu') {
        const iframeRect = iframeRef.current?.getBoundingClientRect();
        setContextMenu({
          x: (iframeRect?.left || 0) + (e.data.x || 0),
          y: (iframeRect?.top || 0) + (e.data.y || 0),
          cssPath: e.data.cssPath || null,
        });
      } else if (e.data.type === 'wf_text_changed') {
        // Inline text edit committed — persist to backend without AI
        const { cssPath, outerHTML } = e.data;
        if (!cssPath || !outerHTML) return;
        axios.post(`${API_URL}/magic/projects/${projectId}/inline-text-edit`, {
          page_slug: currentPageSlugRef.current || 'home',
          css_path: cssPath,
          outer_html: outerHTML,
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } })
          .then(res => {
            if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
          })
          .catch(() => {/* silent — edit already visible in iframe */});
      } else if (e.data.type === 'wf_sections_reordered') {
        // Section drag-and-drop completed — persist new order
        const order = e.data.order;
        if (!Array.isArray(order)) return;
        axios.post(`${API_URL}/magic/projects/${projectId}/sections/reorder`, {
          page_slug: currentPageSlugRef.current || 'home',
          order,
        }, { headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` } })
          .then(res => {
            if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
          })
          .catch((err) => {
            toast.error(err.response?.data?.detail || 'Failed to reorder sections');
          });
      } else if (e.data.type === 'wf_section_action') {
        // Section context menu action
        const { action, sectionIdx } = e.data;
        const pageSlug = currentPageSlugRef.current || 'home';
        const authH = { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` };
        if (action === 'delete') {
          if (!window.confirm('Delete this section? This can be undone.')) return;
          axios.post(`${API_URL}/magic/projects/${projectId}/sections/delete`, { page_slug: pageSlug, section_index: sectionIdx }, { headers: authH })
            .then(res => { if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state); })
            .catch((err) => { toast.error(err.response?.data?.detail || 'Failed to delete section'); });
        } else if (action === 'duplicate') {
          axios.post(`${API_URL}/magic/projects/${projectId}/sections/duplicate`, { page_slug: pageSlug, section_index: sectionIdx }, { headers: authH })
            .then(res => { if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state); })
            .catch((err) => { toast.error(err.response?.data?.detail || 'Failed to duplicate section'); });
        } else if (action === 'move_up' || action === 'move_down') {
          const indexA = action === 'move_up' ? sectionIdx - 1 : sectionIdx;
          const indexB = action === 'move_up' ? sectionIdx : sectionIdx + 1;
          axios.post(`${API_URL}/magic/projects/${projectId}/sections/swap`, { page_slug: pageSlug, index_a: indexA, index_b: indexB }, { headers: authH })
            .then(res => { if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state); })
            .catch((err) => { toast.error(err.response?.data?.detail || 'Failed to move section'); });
        } else if (action === 'regenerate') {
          // TODO Phase 2b: trigger AI section regeneration
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [isMultiPage, generatedPages, isSelectMode, projectId, onEdit]);

  const sendSelectMode = useCallback((value) => {
    try {
      iframeRef.current?.contentWindow?.postMessage({ type: 'wf_set_select_mode', value }, '*');
    } catch (e) { /* iframe not ready */ }
  }, [iframeRef]);

  const handleIframeLoad = useCallback(() => {
    iframeReadyRef.current = false;
  }, []);

  const toggleSelectMode = () => {
    const next = !isSelectMode;
    setIsSelectMode(next);
    if (!next) {
      setSelectedElement(null);
    }
    sendSelectMode(next);
  };

  const exitEditMode = () => {
    setIsSelectMode(false);
    setSelectedElement(null);
    sendSelectMode(false);
  };

  const handleApplyEdit = useCallback(async (requestText) => {
    const text = requestText || editRequest.trim();
    if (!text) return;
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const editCssPath = selectedElement?.cssPath || null;
      const groupId = window.crypto?.randomUUID?.() || `ai-${Date.now()}`;
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit`, {
        page_slug: targetSlug,
        element_html: selectedElement?.outerHTML || null,
        change_request: text,
        css_path: editCssPath,
        preview: true,
        group_id: groupId,
      });
      setAiPreview({
        requestText: text,
        candidateHtml: response.data?.candidate_html,
        updatedPage: response.data?.updated_page,
        groupId: response.data?.group_id || groupId,
        selectedElement,
      });
      toast.success('Preview ready');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Edit failed');
    } finally {
      setIsApplying(false);
    }
  }, [editRequest, isMultiPage, currentPageData, projectId, selectedElement]);

  const handleAcceptPreview = useCallback(async () => {
    if (!aiPreview) return;
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const editCssPath = aiPreview?.selectedElement?.cssPath || selectedElement?.cssPath || null;
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit`, {
        page_slug: targetSlug,
        element_html: aiPreview?.selectedElement?.outerHTML || selectedElement?.outerHTML || null,
        change_request: aiPreview.requestText,
        css_path: editCssPath,
        preview: false,
        candidate_html: aiPreview.candidateHtml || aiPreview.updatedPage?.html || null,
        group_id: aiPreview.groupId,
      });
      toast.success('Changes applied');
      if (editCssPath) {
        setTimeout(() => {
          try {
            iframeRef.current?.contentWindow?.postMessage({ type: 'wf_highlight_changed', cssPath: editCssPath }, '*');
          } catch (e) {}
        }, 300);
      }
      setEditRequest('');
      setAiPreview(null);
      setSelectedElement(null);
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Apply failed');
    } finally {
      setIsApplying(false);
    }
  }, [aiPreview, currentPageData, isMultiPage, onEdit, projectId, selectedElement]);

  const handleRejectPreview = useCallback(() => {
    setAiPreview(null);
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      handleApplyEdit();
    }
  };

  const editHistory = code ? (generatedPages?.length > 0 ? [] : []) : [];
  // Read edit history from magic state passed via onEdit updates
  const magicEditHistory = (() => {
    // Access edit_history from the parent's magic_state via the code/generatedPages props
    // The magic_state is not directly available, but we can reconstruct from the onEdit callback
    // For now, we'll track it from the API responses
    return [];
  })();

  const handleUndo = useCallback(async () => {
    setIsUndoing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/undo`);
      toast.success('Edit undone');
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Undo failed');
    } finally {
      setIsUndoing(false);
    }
  }, [projectId, onEdit]);

  const handleRedo = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/redo`);
      toast.success('Edit re-applied');
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Nothing to redo');
    }
  }, [projectId, onEdit]);

  // Cmd/Ctrl+Z undo and Cmd+Shift+Z redo shortcuts (not in Monaco editor)
  useEffect(() => {
    const handleGlobalKeyDown = (e) => {
      if (activeTab === 'code') return;
      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        handleRedo();
      }
    };
    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, [handleUndo, handleRedo, activeTab]);

  const handleLinkChange = useCallback(async (newHref, openInNewTab) => {
    if (!selectedElement?.cssPath) return;
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit-link`, {
        page_slug: targetSlug,
        css_path: selectedElement.cssPath,
        new_href: newHref,
        target: openInNewTab ? '_blank' : null
      });
      toast.success('Link updated');
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Link update failed');
    } finally {
      setIsApplying(false);
    }
  }, [selectedElement, isMultiPage, currentPageData, projectId, onEdit]);

  const closeVisualPanel = () => {
    exitEditMode();
  };

  const handleDeleteElement = useCallback(async () => {
    if (!selectedElement?.cssPath) return;
    setContextMenu(null);
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/element/delete`, {
        page_slug: targetSlug,
        css_path: selectedElement.cssPath,
      });
      if (onEdit) onEdit(response.data.magic_state);
      setSelectedElement(null);
      toast.success('Element deleted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Delete failed');
    } finally {
      setIsApplying(false);
    }
  }, [selectedElement, isMultiPage, currentPageData, projectId, onEdit]);

  const handleDuplicateElement = useCallback(async () => {
    if (!selectedElement?.cssPath) return;
    setContextMenu(null);
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/element/duplicate`, {
        page_slug: targetSlug,
        css_path: selectedElement.cssPath,
      });
      if (onEdit) onEdit(response.data.magic_state);
      toast.success('Element duplicated');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Duplicate failed');
    } finally {
      setIsApplying(false);
    }
  }, [selectedElement, isMultiPage, currentPageData, projectId, onEdit]);

  const handleCopyStyle = useCallback(() => {
    if (!selectedElement?.computedStyles) return;
    const {
      color,
      backgroundColor,
      backgroundImage,
      fontSize,
      fontWeight,
      fontFamily,
      lineHeight,
      letterSpacing,
      borderRadius,
      borderColor,
      borderWidth,
      boxShadow,
      opacity,
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    } = selectedElement.computedStyles;
    setCopiedStylePayload({
      color,
      backgroundColor,
      backgroundImage,
      fontSize,
      fontWeight,
      fontFamily,
      lineHeight,
      letterSpacing,
      borderRadius,
      borderColor,
      borderWidth,
      boxShadow,
      opacity,
      paddingTop,
      paddingRight,
      paddingBottom,
      paddingLeft,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
    });
    setContextMenu(null);
    toast.success('Style copied');
  }, [selectedElement]);

  const handlePasteStyle = useCallback(async () => {
    if (!selectedElement?.cssPath || !copiedStylePayload) return;
    setContextMenu(null);
    setIsApplying(true);
    try {
      const targetSlug = isMultiPage ? (currentPageData?.slug || 'home') : 'home';
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/element/style`, {
        page_slug: targetSlug,
        css_path: selectedElement.cssPath,
        styles: copiedStylePayload,
      });
      if (onEdit) onEdit(response.data.magic_state);
      toast.success('Style pasted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Paste failed');
    } finally {
      setIsApplying(false);
    }
  }, [selectedElement, copiedStylePayload, isMultiPage, currentPageData, projectId, onEdit]);

  // ── Global Design Edits ──

  const handleGlobalColorChange = async (colorKey, oldColor, newColor) => {
    if (oldColor === newColor) return;
    setIsGlobalEditing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit-global`, {
        change_type: 'color',
        old_value: oldColor,
        new_value: newColor,
        apply_to: 'all_pages'
      });
      toast.success(`${colorKey} color updated across all pages`);
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Color change failed');
    } finally {
      setIsGlobalEditing(false);
    }
  };

  const handleFontChange = async (fontType, oldFont, newFont) => {
    if (oldFont === newFont) return;
    setIsGlobalEditing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit-global`, {
        change_type: 'font',
        font_type: fontType,
        old_value: oldFont,
        new_value: newFont,
        apply_to: 'all_pages'
      });
      toast.success(`${fontType} font updated across all pages`);
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Font change failed');
    } finally {
      setIsGlobalEditing(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/logo`, formData);
      setLogoUrl(response.data.logo_url);
      toast.success('Logo uploaded! Click "Apply to site" to update all pages.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo upload failed');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleApplyLogo = async () => {
    if (!logoUrl) return;
    setIsGlobalEditing(true);
    try {
      const response = await axios.post(`${API_URL}/magic/projects/${projectId}/edit-global`, {
        change_type: 'logo',
        new_value: logoUrl,
        apply_to: 'all_pages'
      });
      toast.success('Logo applied to all pages');
      if (onEdit) onEdit(response.data.magic_state);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Logo apply failed');
    } finally {
      setIsGlobalEditing(false);
    }
  };

  // Viewport container styles
  const viewportContainerStyle = viewport === 'desktop'
    ? 'w-full h-full'
    : viewport === 'tablet'
    ? 'w-[768px] max-h-[1024px] h-full'
    : 'w-[375px] max-h-[812px] h-full';

  // Focus mode: are page controls disabled?
  const pageControlsDisabled = isSelectMode;

  return (
    <>
    <div className="flex" style={{ height: 'calc(100vh - 64px)', background: 'var(--surface-0)' }}>
      {/* Layers Panel — left DOM navigator (hidden when element selected) */}
      {showLayers && !selectedElement && activeTab === 'preview' && (
        <div className="w-[220px] shrink-0 bg-[var(--orbis-bg)] border-r border-[rgba(255,255,255,0.07)] flex flex-col h-full overflow-hidden">
          <LayersPanel
            html={currentPageData?.html || code?.html || ''}
            iframeRef={iframeRef}
            selectedCssPath={selectedElement?.cssPath}
            onSelectElement={() => {
              setIsSelectMode(true);
              sendSelectMode(true);
              setAiPreview(null);
            }}
          />
        </div>
      )}

      {/* Visual Edit Panel — slides in from left when element selected */}
      {selectedElement && activeTab === 'preview' && (
        <VisualEditPanel
          selectedElement={selectedElement}
          onApplyEdit={handleApplyEdit}
          onLinkChange={handleLinkChange}
          isApplying={isApplying}
          iframeRef={iframeRef}
          onClose={closeVisualPanel}
          projectId={projectId}
          currentPageSlug={currentPageSlug}
          onEdit={onEdit}
          reviewEditorState={magicContext?.reviewEditor}
          onAnimationApplied={(newMagicState) => { if (onEdit && newMagicState) onEdit(newMagicState); }}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <Tabs
          value={activeTab}
          onValueChange={(v) => { if (!isSelectMode || v === 'preview') setActiveTab(v); }}
          className="flex flex-col h-full"
        >
          <div className="border-b border-[rgba(255,255,255,0.07)] px-4 flex items-center justify-between">
            <TabsList className="bg-transparent h-12 gap-4">
              <TabsTrigger
                value="preview"
                className="data-[state=active]:bg-transparent data-[state=active]:text-[var(--orbis-t1)] text-[var(--orbis-t4)] gap-2"
              >
                <Eye className="w-4 h-4" />
                Preview
              </TabsTrigger>
              <TabsTrigger
                value="code"
                disabled={isSelectMode}
                className={`data-[state=active]:bg-transparent data-[state=active]:text-[var(--orbis-t1)] text-[var(--orbis-t4)] gap-2 ${
                  isSelectMode ? 'opacity-30 cursor-not-allowed' : ''
                }`}
              >
                <Code2 className="w-4 h-4" />
                Code
              </TabsTrigger>
            </TabsList>

            {/* Preview toolbar */}
            {activeTab === 'preview' && (
              <div className="flex items-center gap-2">
                <ViewportToggle viewport={viewport} onViewportChange={setViewport} />
                <div className="w-px h-5 bg-[var(--orbis-s2)]" />
                {isMultiPage && (
                  <div className={`flex items-center gap-1 bg-[var(--orbis-s1)] rounded-lg p-1 border border-[rgba(255,255,255,0.07)] ${
                    pageControlsDisabled ? 'opacity-40 pointer-events-none' : ''
                  }`}>
                    {generatedPages.map(page => (
                      <button
                        key={page.slug}
                        onClick={() => { if (!pageControlsDisabled) { setCurrentPageSlug(page.slug); setSelectedElement(null); } }}
                        disabled={pageControlsDisabled}
                        className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                          currentPageSlug === page.slug
                            ? 'bg-[var(--orbis-blue)] text-[var(--orbis-bg)]'
                            : 'text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
                        }`}
                      >
                        {page.name}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  onClick={handleOpenBrowserPreview}
                  title="Open current unpublished draft in a new tab"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]"
                >
                  {isPreviewOpening ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Globe className="w-3.5 h-3.5" />}
                  Preview
                </button>
                <div className="relative">
                  <button
                    onClick={() => setVersionsOpen((open) => !open)}
                    title="Browse and restore saved versions"
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                      versionsOpen
                        ? 'bg-[var(--orbis-s2)] border-[rgba(255,255,255,0.18)] text-[var(--orbis-t1)]'
                        : 'border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
                    }`}
                  >
                    <History className="w-3.5 h-3.5" />
                    Versions
                  </button>
                  {versionsOpen && (
                    <div
                      className="absolute right-0 top-[calc(100%+8px)] w-[320px] rounded-2xl border border-[rgba(255,255,255,0.1)] bg-[#111315] shadow-2xl p-3 z-30"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <p className="text-sm font-medium text-[var(--orbis-t1)]">Versions</p>
                          <p className="text-[11px] text-[var(--orbis-t4)]">Named checkpoints for this draft</p>
                        </div>
                        <Button
                          size="sm"
                          onClick={handleCreateVersion}
                          disabled={isSavingVersion}
                          className="h-8 px-3 text-xs bg-[var(--orbis-blue)] hover:bg-[var(--orbis-blue)]/80 text-[#1A1A1A]"
                        >
                          {isSavingVersion ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Save'}
                        </Button>
                      </div>
                      <div className="max-h-[320px] overflow-auto space-y-2">
                        {versionsLoading ? (
                          <div className="flex items-center gap-2 text-xs text-[var(--orbis-t4)] py-4">
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            Loading versions...
                          </div>
                        ) : versions.length === 0 ? (
                          <div className="rounded-xl border border-dashed border-[rgba(255,255,255,0.1)] p-4 text-xs text-[var(--orbis-t4)]">
                            No versions yet. Save one before making a risky change.
                          </div>
                        ) : (
                          versions.map((version) => (
                            <div key={version.version_id} className="rounded-xl border border-[rgba(255,255,255,0.08)] px-3 py-2">
                              <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                  <p className="text-xs font-medium text-[var(--orbis-t1)] truncate">{version.label}</p>
                                  <p className="text-[11px] text-[var(--orbis-t4)] truncate">{version.updated_at ? new Date(version.updated_at).toLocaleString() : version.timestamp}</p>
                                </div>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={isRestoringVersion}
                                  onClick={() => handleRestoreVersion(version.version_id)}
                                  className="h-7 px-2 text-xs text-[var(--orbis-t2)] hover:text-[var(--orbis-t1)]"
                                >
                                  Restore
                                </Button>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={toggleSelectMode}
                  title={isSelectMode ? 'Exit element editing mode' : 'Click any element in the preview to edit it visually'}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    isSelectMode
                      ? 'bg-[var(--orbis-amber)] border-[var(--orbis-amber)] text-[var(--orbis-bg)]'
                      : 'border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:border-[rgba(255,255,255,0.12)] hover:text-[var(--orbis-t1)]'
                  }`}
                >
                  {isSelectMode ? (
                    <><Check className="w-3.5 h-3.5" /> Done Editing</>
                  ) : (
                    <><MousePointer2 className="w-3.5 h-3.5" /> Edit Elements</>
                  )}
                </button>
                <button
                  onClick={handleUndo}
                  disabled={isUndoing}
                  title="Undo last edit (Cmd+Z)"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)] disabled:opacity-40"
                >
                  {isUndoing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Undo2 className="w-3.5 h-3.5" />}
                  Undo
                </button>
                <button
                  onClick={handleRedo}
                  title="Redo last undo (Cmd+Shift+Z)"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]"
                >
                  <Redo2 className="w-3.5 h-3.5" />
                  Redo
                </button>
                <button
                  onClick={() => setShowLayers(l => !l)}
                  title="Toggle layers panel"
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    showLayers
                      ? 'bg-[var(--orbis-s2)] border-[rgba(255,255,255,0.2)] text-[var(--orbis-t1)]'
                      : 'border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]'
                  }`}
                >
                  <Layers className="w-3.5 h-3.5" />
                  Layers
                </button>
                <button
                  onClick={() => setShowSectionPicker(true)}
                  title="Add a new section"
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all border border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]"
                >
                  <LayoutTemplate className="w-3.5 h-3.5" />
                  Add Section
                </button>
              </div>
            )}
          </div>

          <TabsContent value="preview" className="flex-1 mt-0 flex flex-col overflow-hidden data-[state=inactive]:hidden">
            <div className="flex-1 p-4 overflow-hidden flex items-start justify-center bg-[var(--orbis-bg)]/50">
              <div className={`${viewportContainerStyle} rounded-xl overflow-hidden border border-[rgba(255,255,255,0.07)] transition-all duration-300 ease-in-out`}>
                {srcDoc ? (
                  <iframe
                    ref={iframeRef}
                    srcDoc={srcDoc}
                    className="w-full h-full"
                    title="Preview"
                    sandbox="allow-scripts allow-same-origin"
                    onLoad={handleIframeLoad}
                  />
                ) : (
                  <div className="flex items-center justify-center h-full bg-[var(--orbis-s1)]">
                    <p className="text-[var(--orbis-t4)]">No preview available</p>
                  </div>
                )}
              </div>
            </div>

            {aiPreview && (
              <div className="border-t border-[rgba(255,255,255,0.07)] px-4 py-3 bg-[#101214]">
                <div className="flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm text-[var(--orbis-t1)]">AI preview ready</p>
                    <p className="text-[11px] text-[var(--orbis-t4)] truncate">{aiPreview.requestText}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={handleRejectPreview}
                      className="h-8 px-3 text-xs text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]"
                    >
                      Reject
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleAcceptPreview}
                      disabled={isApplying}
                      className="h-8 px-3 text-xs bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)]"
                    >
                      {isApplying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Apply'}
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* AI Edit chat bar — shown when no element is selected */}
            {!selectedElement && (
              <div className="border-t border-[rgba(255,255,255,0.07)] px-4 py-3 bg-[var(--orbis-bg)]/80">
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Describe a change to the page... (⌘+Enter to apply)"
                    value={editRequest}
                    onChange={e => setEditRequest(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 bg-[var(--orbis-s1)] border border-[rgba(255,255,255,0.12)] rounded-lg px-3 py-2 text-sm text-[var(--orbis-t1)] placeholder:text-[var(--orbis-t4)] focus:outline-none focus:border-[rgba(255,255,255,0.12)]"
                  />
                  <Button
                    size="sm"
                    onClick={() => handleApplyEdit()}
                    disabled={!editRequest.trim() || isApplying}
                    className="bg-[var(--orbis-amber)] hover:bg-[var(--orbis-amber)]/80 text-[var(--orbis-bg)] px-4"
                  >
                    {isApplying ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="code" className="flex-1 mt-0 overflow-hidden data-[state=inactive]:hidden">
            <div className="h-full flex">
              {/* File Tree Sidebar */}
              <div className="w-[180px] shrink-0 bg-[var(--orbis-bg)] border-r border-[rgba(255,255,255,0.07)] flex flex-col overflow-hidden">
                <div className="px-3 py-2 border-b border-[rgba(255,255,255,0.07)]">
                  <p className="text-[10px] font-normal text-[var(--orbis-t4)] uppercase tracking-wider">Files</p>
                </div>
                <ScrollArea className="flex-1">
                  <div className="py-1">
                    {virtualFiles?.tree?.map((node, i) => (
                      <FileTreeNode
                        key={i}
                        node={node}
                        activeFile={activeFilePath}
                        onSelect={setActiveFilePath}
                      />
                    ))}
                  </div>
                </ScrollArea>
              </div>

              {/* Monaco Editor */}
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Editor header bar */}
                <div className="flex items-center justify-between px-3 py-1.5 border-b border-[rgba(255,255,255,0.07)] bg-[var(--orbis-s1)]">
                  <div className="flex items-center gap-2 text-xs text-[var(--orbis-t3)]">
                    <FileCode className="w-3.5 h-3.5" />
                    <span>{activeFilePath}</span>
                    {editorDirty && <span className="w-2 h-2 rounded-full bg-[var(--orbis-amber)]" title="Unsaved changes" />}
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={!editorDirty || isSavingCode}
                    onClick={() => {
                      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
                      const content = virtualFiles?.files?.[activeFilePath]?.content || '';
                      saveCode(activeFilePath, content);
                    }}
                    className="h-7 px-2 text-xs text-[var(--orbis-t3)] hover:text-[var(--orbis-t1)]"
                  >
                    {isSavingCode ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    <span className="ml-1">{isSavingCode ? 'Saving...' : 'Save'}</span>
                  </Button>
                </div>

                {/* Monaco */}
                <div className="flex-1">
                  <Editor
                    theme="vs-dark"
                    language={virtualFiles?.files?.[activeFilePath]?.language || 'html'}
                    value={virtualFiles?.files?.[activeFilePath]?.content || ''}
                    onChange={handleEditorChange}
                    onMount={handleEditorMount}
                    options={{
                      fontSize: 13,
                      lineHeight: 20,
                      minimap: { enabled: false },
                      wordWrap: 'on',
                      scrollBeyondLastLine: false,
                      padding: { top: 8 },
                      renderLineHighlight: 'gutter',
                      scrollbar: { verticalScrollbarSize: 8, horizontalScrollbarSize: 8 },
                      overviewRulerBorder: false,
                      bracketPairColorization: { enabled: true },
                    }}
                  />
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* ═══ Right Sidebar — Design Control Panel ═══ */}
      <div className="w-72 border-l border-[rgba(255,255,255,0.07)] flex flex-col shrink-0">
        <ScrollArea className="flex-1">
          <SidebarSection title="Review Context" icon={Globe} iconColor="text-[var(--orbis-blue)]">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="text-[var(--orbis-t4)]">Mode</span>
                <span className="text-[var(--orbis-t1)] capitalize">{mode}</span>
              </div>
              {mode === 'magic' && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--orbis-t4)]">Generation</span>
                    <span className="text-[var(--orbis-t1)]">{generationMode || magicContext?.generationMode || 'landing_page'}</span>
                  </div>
                  {magicContext?.projectName && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--orbis-t4)]">Project</span>
                      <span className="text-[var(--orbis-t1)] truncate ml-3">{magicContext.projectName}</span>
                    </div>
                  )}
                </>
              )}
              {mode === 'template' && templateInfo && (
                <>
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-[var(--orbis-t4)]">Template</span>
                    <span className="text-[var(--orbis-t1)] truncate ml-3">{templateInfo.name || templateInfo.slug || 'Template'}</span>
                  </div>
                  {templateInfo.slug && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-[var(--orbis-t4)]">Slug</span>
                      <span className="text-[var(--orbis-t3)]">{templateInfo.slug}</span>
                    </div>
                  )}
                </>
              )}
              {mode === 'clone' && (
                <p className="text-[11px] text-[var(--orbis-t4)]">
                  Shared review editor with reference-aware AI edits and fidelity context.
                </p>
              )}
            </div>
          </SidebarSection>

          {/* Reference Report — clone mode only */}
          {activeReferenceReport && (
            <SidebarSection title="Reference Report" icon={Layers} iconColor="text-[var(--orbis-blue)]">
              <div className="space-y-3">
                {/* Status row */}
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span style={{ fontSize: '10px', padding: '2px 8px', borderRadius: '100px', background: 'rgba(184,204,223,0.08)', border: '0.5px solid rgba(184,204,223,0.15)', color: 'var(--orbis-blue)' }}>
                    {(activeReferenceReport.intent || '').replace(/_/g, ' ')}
                  </span>
                  <span style={{
                    fontSize: '10px', padding: '2px 8px', borderRadius: '100px',
                    background: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)',
                    color: activeReferenceReport.analysis_status === 'complete' ? '#34D399' : activeReferenceReport.analysis_status === 'blocked' ? '#F87171' : '#FBBF24',
                  }}>
                    {activeReferenceReport.analysis_status || 'complete'}
                  </span>
                </div>

                {/* Fidelity meter */}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t4)' }}>Fidelity</span>
                    <span style={{ fontSize: '11px', color: 'var(--orbis-t3)' }}>
                      {activeReferenceReport.fidelity_applied ?? '?'}%
                      {activeReferenceReport.fidelity_applied !== activeReferenceReport.fidelity_requested && (
                        <span style={{ color: 'var(--orbis-t4)', marginLeft: '4px' }}>(requested {activeReferenceReport.fidelity_requested}%)</span>
                      )}
                    </span>
                  </div>
                  <div style={{ height: '4px', borderRadius: '2px', background: 'var(--orbis-s2)' }}>
                    <div style={{ height: '100%', borderRadius: '2px', background: 'var(--orbis-blue)', width: `${activeReferenceReport.fidelity_applied ?? 0}%`, transition: 'width 0.3s ease' }} />
                  </div>
                </div>

                {/* Preserved */}
                {activeReferenceReport.preserved?.length > 0 && (
                  <div>
                    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t4)', marginBottom: '4px' }}>Preserved</p>
                    <ul className="space-y-1">
                      {activeReferenceReport.preserved.map((p, i) => (
                        <li key={i} className="flex items-start gap-1.5" style={{ fontSize: '11px', color: 'var(--orbis-t3)' }}>
                          <Check className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#34D399' }} />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Transformed */}
                {activeReferenceReport.transformed?.length > 0 && (
                  <div>
                    <p style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--orbis-t4)', marginBottom: '4px' }}>Transformed</p>
                    <ul className="space-y-1">
                      {activeReferenceReport.transformed.map((t, i) => (
                        <li key={i} className="flex items-start gap-1.5" style={{ fontSize: '11px', color: 'var(--orbis-t3)' }}>
                          <ChevronRight className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: 'var(--orbis-blue)' }} />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Access limits — collapsible, only shown when there are items */}
                {activeReferenceReport.missing_due_to_limited_access?.length > 0 && (
                  <details style={{ fontSize: '11px', color: 'var(--orbis-t4)' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: '#FBBF24', marginBottom: '4px' }}>
                      Access limits ({activeReferenceReport.missing_due_to_limited_access.length})
                    </summary>
                    <ul className="space-y-1 mt-1">
                      {activeReferenceReport.missing_due_to_limited_access.map((m, i) => (
                        <li key={i} style={{ fontSize: '11px', color: 'var(--orbis-t4)' }}>· {m}</li>
                      ))}
                    </ul>
                  </details>
                )}

                {/* Warnings */}
                {activeReferenceReport.warnings?.length > 0 && (
                  <div className="space-y-1">
                    {activeReferenceReport.warnings.map((w, i) => (
                      <p key={i} style={{ fontSize: '10px', color: '#FBBF24', lineHeight: 1.4 }}>⚠ {w}</p>
                    ))}
                  </div>
                )}
              </div>
            </SidebarSection>
          )}

          {/* Brand / Logo */}
          <SidebarSection title="Brand" icon={ImageIcon} iconColor="text-[var(--orbis-amber)]">
            <div className="space-y-2">
              {logoUrl ? (
                <div className="space-y-2">
                  <div className="bg-[var(--orbis-s1)] rounded-lg p-3 flex items-center justify-center border border-[rgba(255,255,255,0.07)]">
                    <img src={`${process.env.REACT_APP_BACKEND_URL}${logoUrl}`} alt="Logo" className="max-h-12 max-w-full object-contain" />
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => logoInputRef.current?.click()} className="flex-1 text-xs border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)]">
                      Replace
                    </Button>
                    <Button size="sm" onClick={handleApplyLogo} disabled={isGlobalEditing} className="flex-1 text-xs bg-[var(--orbis-blue)] hover:bg-[var(--orbis-blue)]/80 text-[#1A1A1A]">
                      {isGlobalEditing ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply to site'}
                    </Button>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => logoInputRef.current?.click()}
                  disabled={isUploadingLogo}
                  className="w-full border-2 border-dashed border-[rgba(255,255,255,0.12)] rounded-lg p-4 text-center hover:border-[rgba(255,255,255,0.12)] transition-colors"
                >
                  {isUploadingLogo ? (
                    <Loader2 className="w-5 h-5 text-[var(--orbis-t4)] mx-auto animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 text-[var(--orbis-t4)] mx-auto mb-1" />
                  )}
                  <p className="text-[11px] text-[var(--orbis-t4)]">Upload logo</p>
                </button>
              )}
              <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" />
            </div>
          </SidebarSection>

          {/* Design System — Editable */}
          <SidebarSection title="Colors" icon={Palette} iconColor="text-pink-400">
            <div className="space-y-2">
              {isGlobalEditing && (
                <div className="flex items-center gap-2 text-xs text-[var(--orbis-amber)] mb-2">
                  <Loader2 className="w-3 h-3 animate-spin" /> Updating all pages...
                </div>
              )}
              {[
                { key: 'primary', label: 'Primary' },
                { key: 'secondary', label: 'Secondary' },
                { key: 'accent', label: 'Accent' },
                { key: 'background', label: 'Background' },
                { key: 'text', label: 'Text' },
              ].map(({ key, label }) => colors[key] && (
                <EditableColorSwatch
                  key={key}
                  color={colors[key]}
                  label={label}
                  disabled={isGlobalEditing}
                  onChange={(newColor) => handleGlobalColorChange(label, colors[key], newColor)}
                  projectId={projectId}
                />
              ))}
              {!Object.keys(colors).length && (
                <p className="text-[11px] text-[var(--orbis-t4)]">
                  No color palette loaded for this draft yet.
                </p>
              )}

              {/* Mesh Gradient toggle */}
              <button
                onClick={() => setShowMeshEditor(v => !v)}
                className="w-full flex items-center justify-between text-[11px] py-1.5 px-2 rounded-md transition-colors"
                style={{
                  background: showMeshEditor ? 'var(--orbis-blue)' : 'var(--orbis-s3)',
                  color: showMeshEditor ? 'var(--orbis-bg)' : 'var(--orbis-t3)',
                }}
              >
                <span>Mesh Gradient</span>
                <span className="text-[9px] opacity-70">{showMeshEditor ? '▲ close' : '▼ open'}</span>
              </button>

              {showMeshEditor && (
                <MeshGradientEditor
                  projectId={projectId}
                  selectedElement={selectedElement}
                  currentPageSlug={currentPageSlug}
                  meshGradients={modeContext?.reviewEditor?.mesh_gradients || {}}
                  onApplied={(newMagicState) => {
                    if (onEdit && newMagicState) onEdit(newMagicState);
                  }}
                  onClose={() => setShowMeshEditor(false)}
                />
              )}
            </div>
          </SidebarSection>

          {/* Typography */}
          <SidebarSection title="Typography" icon={Type} iconColor="text-blue-400" defaultOpen={false}>
            <div className="space-y-3">
              {isGlobalEditing && (
                <div className="flex items-center gap-2 text-xs text-[var(--orbis-amber)]">
                  <Loader2 className="w-3 h-3 animate-spin" /> Updating fonts...
                </div>
              )}
              <div>
                <p className="text-[11px] text-[var(--orbis-t4)] mb-1">Heading font</p>
                <FontSearchPicker
                  value={typography.heading || 'Inter'}
                  onChange={(font) => handleFontChange('heading', typography.heading, font)}
                />
                <VariableFontAxesControl
                  fontRole="heading"
                  fontFamily={typography.heading || 'Inter'}
                  currentAxes={modeContext?.variableFontAxes?.heading}
                  projectId={projectId}
                  onApplied={(newMagicState) => { if (onEdit && newMagicState) onEdit(newMagicState); }}
                />
              </div>
              <div>
                <p className="text-[11px] text-[var(--orbis-t4)] mb-1">Body font</p>
                <FontSearchPicker
                  value={typography.body || 'Inter'}
                  onChange={(font) => handleFontChange('body', typography.body, font)}
                />
                <VariableFontAxesControl
                  fontRole="body"
                  fontFamily={typography.body || 'Inter'}
                  currentAxes={modeContext?.variableFontAxes?.body}
                  projectId={projectId}
                  onApplied={(newMagicState) => { if (onEdit && newMagicState) onEdit(newMagicState); }}
                />
              </div>
              {designSystem?.style?.name && (
                <div>
                  <p className="text-[11px] text-[var(--orbis-t4)] mb-1">Style</p>
                  <p className="text-xs text-[var(--orbis-t3)]">{designSystem.style.name}</p>
                </div>
              )}
            </div>
          </SidebarSection>

          {/* Pages — condensed, disabled in edit mode */}
          {isMultiPage && (
            <SidebarSection title={`Pages (${generatedPages?.length})`} icon={Globe} iconColor="text-emerald-400" defaultOpen={false}>
              <div className={`space-y-0.5 ${pageControlsDisabled ? 'opacity-40 pointer-events-none' : ''}`}>
                {generatedPages.map(page => (
                  <button
                    key={page.slug}
                    onClick={() => { if (!pageControlsDisabled) { setCurrentPageSlug(page.slug); setActiveTab('preview'); } }}
                    disabled={pageControlsDisabled}
                    className={`w-full flex items-center justify-between p-2 rounded-md text-xs transition-all ${
                      currentPageSlug === page.slug
                        ? 'bg-[var(--orbis-blue)]/10 text-[var(--orbis-t1)]'
                        : 'text-[var(--orbis-t3)] hover:bg-[var(--orbis-s2)]'
                    }`}
                  >
                    <span>{page.name}</span>
                    <ChevronRight className="w-3 h-3" />
                  </button>
                ))}
              </div>
              {pageControlsDisabled && (
                <p className="text-[10px] text-[var(--orbis-t4)] mt-2 italic">Exit edit mode to switch pages</p>
              )}
            </SidebarSection>
          )}

          {/* SEO / Meta */}
          <SidebarSection title="SEO & Meta" icon={Search} iconColor="text-purple-400" defaultOpen={false}>
            <SeoPanel projectId={projectId} currentPageSlug={currentPageSlug} onEdit={onEdit} />
          </SidebarSection>

          {/* Navigation Editor */}
          <SidebarSection title="Navigation" icon={Navigation} iconColor="text-blue-400" defaultOpen={false}>
            <NavEditorPanel projectId={projectId} currentPageSlug={currentPageSlug} onEdit={onEdit} />
          </SidebarSection>

          {/* Forms */}
          <SidebarSection title="Forms" icon={Mail} iconColor="text-orange-400" defaultOpen={false}>
            <div className="space-y-3">
              <div>
                <label className="text-[10px] text-[var(--orbis-t3)] uppercase tracking-wider mb-1 block">Recipient Email</label>
                <input
                  type="email"
                  value={formSettings.recipient_email}
                  onChange={e => setFormSettings(prev => ({ ...prev, recipient_email: e.target.value }))}
                  placeholder="info@company.com"
                  className="w-full px-2.5 py-1.5 rounded-md bg-[var(--orbis-s2)] border border-[rgba(255,255,255,0.08)] text-[var(--orbis-t1)] text-xs placeholder:text-[var(--orbis-t4)] focus:outline-none focus:ring-1 focus:ring-[var(--orbis-blue)]/50"
                />
              </div>
              <div>
                <label className="text-[10px] text-[var(--orbis-t3)] uppercase tracking-wider mb-1.5 block">Form Mode</label>
                <div className="flex gap-1">
                  {['orbis', 'embed'].map(mode => (
                    <button
                      key={mode}
                      onClick={() => setFormSettings(prev => ({ ...prev, mode }))}
                      className={`flex-1 px-2 py-1.5 rounded-md text-xs transition-all ${
                        formSettings.mode === mode
                          ? 'bg-[var(--orbis-blue)]/15 text-[var(--orbis-blue)] border border-[var(--orbis-blue)]/30'
                          : 'bg-[var(--orbis-s2)] text-[var(--orbis-t3)] border border-transparent hover:bg-[var(--orbis-s3)]'
                      }`}
                    >
                      {mode === 'orbis' ? 'Orbis Form' : 'Embed External'}
                    </button>
                  ))}
                </div>
              </div>
              {formSettings.mode === 'embed' && (
                <div>
                  <label className="text-[10px] text-[var(--orbis-t3)] uppercase tracking-wider mb-1 block">Embed Code</label>
                  <textarea
                    value={formSettings.embed_code}
                    onChange={e => setFormSettings(prev => ({ ...prev, embed_code: e.target.value }))}
                    placeholder="Paste iframe or embed code (GHL, HubSpot, Typeform...)"
                    rows={4}
                    className="w-full px-2.5 py-1.5 rounded-md bg-[var(--orbis-s2)] border border-[rgba(255,255,255,0.08)] text-[var(--orbis-t1)] text-xs placeholder:text-[var(--orbis-t4)] focus:outline-none focus:ring-1 focus:ring-[var(--orbis-blue)]/50 resize-none font-mono"
                  />
                </div>
              )}
              <Button
                size="sm"
                disabled={isSavingFormSettings}
                onClick={async () => {
                  setIsSavingFormSettings(true);
                  try {
                    const res = await axios.post(`${API_URL}/magic/projects/${projectId}/form-settings`, formSettings, {
                      headers: { Authorization: `Bearer ${localStorage.getItem('orbis_token')}` }
                    });
                    toast.success('Form settings saved');
                    if (res.data?.form_settings) setFormSettings(res.data.form_settings);
                    // If mode changed and HTML was updated, propagate to parent
                    if (onEdit && res.data?.magic_state) onEdit(res.data.magic_state);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to save form settings');
                  } finally {
                    setIsSavingFormSettings(false);
                  }
                }}
                className="w-full bg-[var(--orbis-s2)] hover:bg-[var(--orbis-s3)] text-[var(--orbis-t2)] text-xs border border-[rgba(255,255,255,0.08)]"
              >
                {isSavingFormSettings ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Save className="w-3 h-3 mr-1.5" />}
                Save Form Settings
              </Button>
              <Button
                size="sm"
                disabled={isSendingTestForm || !formSettings.recipient_email}
                onClick={async () => {
                  if (!formSettings.recipient_email) {
                    toast.error('Set a recipient email first');
                    return;
                  }
                  setIsSendingTestForm(true);
                  try {
                    await axios.post(`${API_URL}/forms/${projectId}/submit`, {
                      name: 'Test User',
                      email: 'test@orbis.build',
                      message: 'This is a test submission from the Orbis Review panel.',
                    }, { headers: { 'Content-Type': 'application/json' } });
                    toast.success(`Test email sent to ${formSettings.recipient_email}`);
                  } catch (e) {
                    toast.error(e.response?.data?.detail || 'Failed to send test');
                  } finally {
                    setIsSendingTestForm(false);
                  }
                }}
                className="w-full bg-transparent hover:bg-[var(--orbis-s2)] text-[var(--orbis-t3)] text-xs border border-[rgba(255,255,255,0.08)]"
              >
                {isSendingTestForm ? <Loader2 className="w-3 h-3 mr-1.5 animate-spin" /> : <Send className="w-3 h-3 mr-1.5" />}
                Send Test
              </Button>
            </div>
          </SidebarSection>

          {/* Quality Checks */}
          <SidebarSection title="Quality" icon={CheckCircle2} iconColor="text-emerald-400" defaultOpen={false}>
            <div className="space-y-1">
              {['Responsive design', 'Accessibility', 'SEO meta tags', 'Performance optimized'].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                  <span className="text-[var(--orbis-t3)]">{item}</span>
                </div>
              ))}
            </div>
          </SidebarSection>
        </ScrollArea>

        {/* Actions — always visible at bottom */}
        <div className="p-3 border-t border-[rgba(255,255,255,0.07)] space-y-2">
          <Button
            onClick={async () => {
              setIsApproving(true);
              try { await onApprove?.(); } finally { setIsApproving(false); }
            }}
            disabled={isApproving || isSelectMode}
            className="w-full h-11 bg-[var(--orbis-blue)] hover:bg-[var(--orbis-blue)]/80 disabled:opacity-50 text-[#1A1A1A] text-sm"
          >
            {isApproving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ChevronRight className="w-4 h-4 mr-2" />}
            Approve & Deploy
          </Button>
          <Button
            variant="outline"
            onClick={onDownload}
            className="w-full border-[rgba(255,255,255,0.12)] text-[var(--orbis-t3)] hover:bg-[var(--orbis-s2)] text-xs"
          >
            <Download className="w-4 h-4 mr-2" />
            Download ZIP
          </Button>
        </div>
      </div>
    </div>

    {/* Section Picker modal */}
    {showSectionPicker && (
      <SectionPicker
        projectId={projectId}
        currentPageSlug={currentPageSlug}
        onInserted={(newMagicState) => {
          if (onEdit) onEdit(newMagicState);
        }}
        onClose={() => setShowSectionPicker(false)}
      />
    )}

    {contextMenu && selectedElement && (
      <div
        className="fixed z-[80] w-56 rounded-xl border border-[rgba(255,255,255,0.1)] bg-[#121416] p-1.5 shadow-2xl"
        style={{ left: contextMenu.x, top: contextMenu.y }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={handleDuplicateElement}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-[var(--orbis-t2)] hover:bg-[var(--orbis-s2)]"
        >
          Duplicate element
        </button>
        <button
          onClick={handleDeleteElement}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-red-300 hover:bg-[var(--orbis-s2)]"
        >
          Delete element
        </button>
        <button
          onClick={handleCopyStyle}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-[var(--orbis-t2)] hover:bg-[var(--orbis-s2)]"
        >
          Copy style
        </button>
        <button
          onClick={handlePasteStyle}
          disabled={!copiedStylePayload}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-[var(--orbis-t2)] hover:bg-[var(--orbis-s2)] disabled:opacity-40"
        >
          Paste style
        </button>
        <button
          onClick={() => {
            setContextMenu(null);
            setEditRequest(`Refine this ${selectedElement.tagName} element: `);
            setActiveTab('preview');
          }}
          className="w-full text-left rounded-lg px-3 py-2 text-xs text-[var(--orbis-blue)] hover:bg-[var(--orbis-s2)]"
        >
          Ask AI about this element
        </button>
      </div>
    )}
    </>
  );
}
